import React, { useState, useEffect } from 'react';
import { Layout, Plus, Clock, Edit2, Trash2, Calendar, Tag, Folder, MoreHorizontal } from 'lucide-react';
import { useModal } from '../ui/ModalProvider';

export default function AdminKanbanBoard() {
  const { showConfirm } = useModal();
  const [employees, setEmployees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null); // null = Global Board
  
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskAssignee, setNewTaskAssignee] = useState('');
  const [newTaskTags, setNewTaskTags] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProjectData, setNewProjectData] = useState({ title: '', description: '' });
  
  // Advanced Modal State
  const [editingTask, setEditingTask] = useState(null);
  const [editFormData, setEditFormData] = useState({ 
    title: '', description: '', priority: 'medium', dueDate: '', tags: '', employeeId: '', projectId: '' 
  });

  const COLUMNS = [
    { id: 'todo', label: 'Todo', color: 'bg-[#563e7c]' }, // GitHub-ish column marker colors
    { id: 'in_progress', label: 'In Progress', color: 'bg-[#9e6a03]' },
    { id: 'review', label: 'Review', color: 'bg-[#238636]' },
    { id: 'done', label: 'Done', color: 'bg-[#8957e5]' }
  ];

  useEffect(() => {
    fetchEmployees();
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [selectedProjectId]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/employees/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setEmployees(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setProjects(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = selectedProjectId 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/tasks?projectId=${selectedProjectId}` 
        : `${process.env.REACT_APP_BACKEND_URL}/api/tasks`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setTasks(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle) return;
    try {
      const token = localStorage.getItem('token');
      const payload = { 
        title: newTaskTitle, 
        priority: 'medium',
        employeeId: newTaskAssignee ? Number(newTaskAssignee) : null,
        projectId: selectedProjectId ? Number(selectedProjectId) : null,
        tags: newTaskTags.split(',').map(t => t.trim()).filter(t => t)
      };

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setNewTaskTitle('');
        setNewTaskAssignee('');
        setNewTaskTags('');
        setShowAddForm(false);
        fetchTasks();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectData.title || !newProjectData.description) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ 
          title: newProjectData.title, 
          description: newProjectData.description,
          tags: [],
          image: null,
          link: null
        })
      });
      if (res.ok) {
        const created = await res.json();
        setNewProjectData({ title: '', description: '' });
        setShowNewProjectModal(false);
        fetchProjects();
        setSelectedProjectId(created.id);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDrop = async (e, newStatus) => {
    const taskId = e.dataTransfer.getData('taskId');
    if (!taskId) return;
    
    // Optimistic UI update
    setTasks(prev => prev.map(t => t.id === Number(taskId) ? { ...t, status: newStatus } : t));
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
    } catch (err) {
      console.error(err);
      fetchTasks(); // Revert on failure
    }
  };

  const handleDragStart = (e, taskId) => {
    e.dataTransfer.setData('taskId', taskId);
  };

  // --- Modal Functions ---
  const openModal = (task) => {
    setEditingTask(task);
    setEditFormData({
      title: task.title,
      description: task.description || '',
      priority: task.priority || 'medium',
      dueDate: task.dueDate ? new Date(task.dueDate).toISOString().slice(0, 16) : '',
      tags: task.tags ? task.tags.join(', ') : '',
      employeeId: task.employeeId || '',
      projectId: task.projectId || ''
    });
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${editingTask.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: editFormData.title,
          description: editFormData.description,
          priority: editFormData.priority,
          dueDate: editFormData.dueDate ? new Date(editFormData.dueDate).toISOString() : null,
          employeeId: editFormData.employeeId ? Number(editFormData.employeeId) : null,
          projectId: editFormData.projectId ? Number(editFormData.projectId) : null,
          tags: editFormData.tags.split(',').map(t => t.trim()).filter(t => t)
        })
      });
      if (res.ok) {
        setEditingTask(null);
        fetchTasks();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteTask = () => {
    showConfirm('Are you sure you want to delete this task? This cannot be undone.', async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${editingTask.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          setEditingTask(null);
          fetchTasks();
        }
      } catch (err) {
        console.error(err);
      }
    });
  };

  // GitHub style tag colors
  const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 60%, 40%)`;
  };

  return (
    <div className="space-y-6 text-[#c9d1d9] font-sans">
      
      {/* Header Area (GitHub style) */}
      <div className="flex flex-col gap-4 border-b border-[#30363d] pb-5">
        <div className="flex justify-between items-center">
          <h2 className="text-[20px] font-semibold text-white flex items-center">
            <Layout className="w-5 h-5 mr-3 text-[#8b949e]" />
            Task Board
          </h2>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setSelectedProjectId(null)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors border ${!selectedProjectId ? 'bg-[#21262d] border-[#30363d] text-white' : 'border-transparent text-[#8b949e] hover:bg-[#21262d] hover:text-white'}`}
            >
              Global Tasks
            </button>
            {projects.map(proj => (
              <button 
                key={proj.id}
                onClick={() => setSelectedProjectId(proj.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors border ${selectedProjectId === proj.id ? 'bg-[#21262d] border-[#30363d] text-white' : 'border-transparent text-[#8b949e] hover:bg-[#21262d] hover:text-white'}`}
              >
                {proj.title}
              </button>
            ))}
            
            <button 
              onClick={() => setShowNewProjectModal(true)} 
              className="ml-2 text-[#8b949e] hover:text-white p-1 rounded-md hover:bg-[#21262d] transition-colors"
              title="Create New Board"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          
          <button 
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-[#238636] text-white px-3 py-1.5 rounded-md text-sm font-semibold hover:bg-[#2ea043] transition-colors flex items-center border border-[rgba(240,246,252,0.1)]"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New item
          </button>
        </div>
      </div>

      {/* GitHub Style Add Form (appears below header if toggled) */}
      {showAddForm && (
        <form onSubmit={handleAddTask} className="flex flex-wrap gap-3 bg-[#161b22] p-4 rounded-md border border-[#30363d] items-center mb-6">
          <input
            type="text"
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            placeholder="Type a task title..."
            className="flex-1 bg-[#0d1117] border border-[#30363d] rounded-md px-3 py-1.5 text-sm text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff] focus:ring-1 focus:ring-[#58a6ff] min-w-[200px]"
            required
            autoFocus
          />
          <select 
            className="bg-[#0d1117] border border-[#30363d] text-[#c9d1d9] rounded-md px-3 py-1.5 text-sm outline-none focus:border-[#58a6ff]"
            value={newTaskAssignee}
            onChange={e => setNewTaskAssignee(e.target.value)}
          >
            <option value="">Assignee</option>
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>{emp.email.split('@')[0]}</option>
            ))}
          </select>
          <div className="relative w-48">
            <Tag className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8b949e]" />
            <input
              type="text"
              value={newTaskTags}
              onChange={(e) => setNewTaskTags(e.target.value)}
              placeholder="Labels (csv)"
              className="w-full bg-[#0d1117] border border-[#30363d] rounded-md pl-8 pr-3 py-1.5 text-sm text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff]"
            />
          </div>
          <button type="submit" className="bg-[#21262d] text-[#c9d1d9] px-3 py-1.5 rounded-md text-sm font-semibold hover:bg-[#30363d] hover:text-white transition-colors border border-[#30363d]">
            Add
          </button>
        </form>
      )}

      {/* Kanban Board Area */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {COLUMNS.map(col => (
          <div 
            key={col.id}
            onDragOver={e => e.preventDefault()}
            onDrop={e => handleDrop(e, col.id)}
            className="flex-shrink-0 w-80 bg-transparent flex flex-col min-h-[400px]"
          >
            {/* Column Header */}
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-semibold text-[#c9d1d9] flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${col.color}`}></span>
                {col.label}
                <span className="ml-2 bg-[#161b22] text-[#8b949e] border border-[#30363d] text-xs px-2 py-0.5 rounded-full font-medium">
                  {tasks.filter(t => t.status === col.id).length}
                </span>
              </h3>
              <MoreHorizontal className="w-4 h-4 text-[#8b949e] cursor-pointer hover:text-white" />
            </div>
            
            {/* Column Cards */}
            <div className="space-y-2 flex-1">
              {tasks.filter(t => t.status === col.id).map(task => (
                <div 
                  key={task.id}
                  draggable
                  onDragStart={e => handleDragStart(e, task.id)}
                  onClick={() => openModal(task)}
                  className="bg-[#161b22] border border-[#30363d] p-3 rounded-md cursor-pointer hover:border-[#8b949e] transition-colors relative group"
                >
                  
                  <div className="text-[14px] text-[#c9d1d9] font-medium leading-5 mb-2 pr-4">
                    {task.title}
                  </div>
                  
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {task.tags && task.tags.map(tag => (
                      <span key={tag} 
                        className="text-[11px] px-2 py-0.5 rounded-full border border-[rgba(255,255,255,0.1)] text-white font-medium shadow-sm"
                        style={{ backgroundColor: stringToColor(tag) }}>
                        {tag}
                      </span>
                    ))}
                    {!selectedProjectId && task.projectId && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full border border-[#30363d] bg-[#21262d] text-[#8b949e] font-medium flex items-center">
                        <Folder className="w-3 h-3 mr-1" /> Project
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between mt-3 text-xs text-[#8b949e]">
                    <div className="flex items-center gap-3">
                      {task.priority && (
                        <span className={`font-semibold ${task.priority === 'high' ? 'text-red-400' : task.priority === 'low' ? 'text-green-400' : 'text-yellow-400'}`}>
                          {task.priority === 'high' ? 'High' : task.priority === 'low' ? 'Low' : 'Med'}
                        </span>
                      )}
                      {task.dueDate && (
                        <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" /> {new Date(task.dueDate).toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}</span>
                      )}
                    </div>

                    {task.assigneeEmail ? (
                      <div className="w-6 h-6 rounded-full bg-[#30363d] border border-[#484f58] flex items-center justify-center text-[10px] font-bold text-white shadow-sm" title={task.assigneeEmail}>
                        {task.assigneeEmail.charAt(0).toUpperCase()}
                      </div>
                    ) : (
                      <div className="text-[11px] italic text-[#8b949e]">Unassigned</div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* GitHub style ghost add item at bottom */}
              <button 
                onClick={() => {
                  setShowAddForm(true);
                  // small scroll to top trick since form is at top
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="w-full text-left text-sm text-[#8b949e] hover:text-[#c9d1d9] hover:bg-[#161b22] px-2 py-1.5 rounded-md transition-colors flex items-center mt-1"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Add item
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Advanced Task Modal (GitHub Style) */}
      {editingTask && (
        <div className="fixed inset-0 bg-[#010409]/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#161b22] border border-[#30363d] rounded-xl w-full max-w-2xl p-0 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            
            <div className="border-b border-[#30363d] px-6 py-4 flex justify-between items-center bg-[#0d1117]">
              <h3 className="text-lg font-semibold text-[#c9d1d9] flex items-center">
                <Edit2 className="w-4 h-4 mr-2 text-[#8b949e]" />
                Edit Item
              </h3>
              <button onClick={() => setEditingTask(null)} className="text-[#8b949e] hover:text-white">✕</button>
            </div>
            
            <form onSubmit={handleUpdateTask} className="p-6 overflow-y-auto custom-scrollbar flex-1 flex flex-col gap-5">
              
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-[#c9d1d9]">Title</label>
                <input
                  type="text"
                  value={editFormData.title}
                  onChange={e => setEditFormData({...editFormData, title: e.target.value})}
                  className="w-full bg-[#0d1117] border border-[#30363d] rounded-md px-3 py-1.5 text-sm text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff] focus:ring-1 focus:ring-[#58a6ff]"
                  required
                />
              </div>
              
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-[#c9d1d9]">Description</label>
                <textarea
                  value={editFormData.description}
                  onChange={e => setEditFormData({...editFormData, description: e.target.value})}
                  className="w-full bg-[#0d1117] border border-[#30363d] rounded-md px-3 py-2 text-sm text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff] min-h-[120px] font-mono"
                  placeholder="Leave a comment"
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-[#c9d1d9]">Assignee</label>
                  <select 
                    className="w-full bg-[#0d1117] border border-[#30363d] rounded-md px-3 py-1.5 text-sm text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff]"
                    value={editFormData.employeeId}
                    onChange={e => setEditFormData({...editFormData, employeeId: e.target.value})}
                  >
                    <option value="">Unassigned</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.email}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-[#c9d1d9]">Project</label>
                  <select 
                    className="w-full bg-[#0d1117] border border-[#30363d] rounded-md px-3 py-1.5 text-sm text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff]"
                    value={editFormData.projectId}
                    onChange={e => setEditFormData({...editFormData, projectId: e.target.value})}
                  >
                    <option value="">No Project</option>
                    {projects.map(proj => (
                      <option key={proj.id} value={proj.id}>{proj.title}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-[#c9d1d9]">Priority</label>
                  <select
                    value={editFormData.priority}
                    onChange={e => setEditFormData({...editFormData, priority: e.target.value})}
                    className="w-full bg-[#0d1117] border border-[#30363d] rounded-md px-3 py-1.5 text-sm text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff]"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-[#c9d1d9]">Due Date</label>
                  <input
                    type="datetime-local"
                    value={editFormData.dueDate}
                    onChange={e => setEditFormData({...editFormData, dueDate: e.target.value})}
                    className="w-full bg-[#0d1117] border border-[#30363d] rounded-md px-3 py-1.5 text-sm text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff] [color-scheme:dark]"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-[#c9d1d9]">Labels</label>
                <input
                  type="text"
                  value={editFormData.tags}
                  onChange={e => setEditFormData({...editFormData, tags: e.target.value})}
                  placeholder="e.g. bug, enhancement"
                  className="w-full bg-[#0d1117] border border-[#30363d] rounded-md px-3 py-1.5 text-sm text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff]"
                />
              </div>

            </form>
            
            <div className="border-t border-[#30363d] px-6 py-4 flex justify-between items-center bg-[#0d1117]">
              <button
                type="button"
                onClick={handleDeleteTask}
                className="text-[#f85149] hover:text-white hover:bg-[#da3633] px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
              >
                Delete item
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEditingTask(null)}
                  className="px-3 py-1.5 text-[#c9d1d9] bg-[#21262d] border border-[#30363d] hover:bg-[#30363d] rounded-md text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateTask}
                  className="px-3 py-1.5 text-white bg-[#238636] hover:bg-[#2ea043] border border-[rgba(240,246,252,0.1)] rounded-md text-sm font-medium transition-colors"
                >
                  Save changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Project (Board) Modal */}
      {showNewProjectModal && (
        <div className="fixed inset-0 bg-[#010409]/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#161b22] border border-[#30363d] rounded-xl w-full max-w-md p-0 shadow-2xl overflow-hidden flex flex-col">
            <div className="border-b border-[#30363d] px-6 py-4 flex justify-between items-center bg-[#0d1117]">
              <h3 className="text-lg font-semibold text-[#c9d1d9] flex items-center">
                <Layout className="w-4 h-4 mr-2 text-[#8b949e]" />
                New Kanban Board
              </h3>
              <button onClick={() => setShowNewProjectModal(false)} className="text-[#8b949e] hover:text-white">✕</button>
            </div>
            <form onSubmit={handleCreateProject} className="p-6 flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-[#c9d1d9]">Board Name</label>
                <input
                  type="text"
                  value={newProjectData.title}
                  onChange={e => setNewProjectData({...newProjectData, title: e.target.value})}
                  className="w-full bg-[#0d1117] border border-[#30363d] rounded-md px-3 py-1.5 text-sm text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff] focus:ring-1 focus:ring-[#58a6ff]"
                  placeholder="e.g., Q3 Marketing Campaign"
                  required
                  autoFocus
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-[#c9d1d9]">Description</label>
                <textarea
                  value={newProjectData.description}
                  onChange={e => setNewProjectData({...newProjectData, description: e.target.value})}
                  className="w-full bg-[#0d1117] border border-[#30363d] rounded-md px-3 py-2 text-sm text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff]"
                  placeholder="What is this board for?"
                  required
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowNewProjectModal(false)}
                  className="px-3 py-1.5 text-[#c9d1d9] bg-[#21262d] border border-[#30363d] hover:bg-[#30363d] rounded-md text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 text-white bg-[#238636] hover:bg-[#2ea043] border border-[rgba(240,246,252,0.1)] rounded-md text-sm font-medium transition-colors"
                >
                  Create Board
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
